import React, { FunctionComponent, useCallback } from 'react';

import { Button } from '@yandex-360/orb-components/dist/components/Button';
import { ListItem } from '@yandex-360/orb-components/dist/components/ListItem';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import { selectMicrophoneDevicePermission } from '../../../../../../../../pages/call/features/device/selectors';
import { getExternalLinks } from '../../../../../../../helpers/external-links';
import { i18n } from '../../../../../../../helpers/i18n';
import { ACTION, FEATURE } from '../../../../../../../helpers/logger-enums';
import { createLogger } from '../../../../../../../helpers/loggerFacade';
import { useActions } from '../../../../../../../hooks';
import { useTypedSelector } from '../../../../../../../hooks/useTypedSelector';
import { getAudioInputError } from '../../../../../../device-manager/selectors';
import { selectIsUserIODiagnosticsEnabled } from '../../../../../../flags/selectors';
import { deviceManager } from '../../../../../../goloom-sdk';
import {
    getAudioInputDeviceSelector,
    getDevicesByTypeSelector,
    setAudioInputDeviceAction,
} from '../../../../../../settings';
import { DEVICE_DENIED_ERRORS } from '../../../../../constants';
import { SettingsSelect } from '../../../settings-select';
import { InputDeviceDiagnosticButton } from '../sound-with-diagnostic/input-device-section/input-device-diagnostic-button';
import styles from './styles.css';

const SELECT_DEVICE_NOT_FOUND_KEY = 'DEVICE_NOT_FOUND';

const logger = createLogger('settings-modal-v2-sound-input');

export const InputDeviceSelect: FunctionComponent = () => {
    const { setAudioInputDevice } = useActions({ setAudioInputDevice: setAudioInputDeviceAction });

    const inputDevices = useTypedSelector((state) => getDevicesByTypeSelector(state, 'audioinput'));
    const isDiagnosticAvailable = useSelector(selectIsUserIODiagnosticsEnabled);
    const currentInputDeviceId = useSelector(getAudioInputDeviceSelector)?.deviceId;
    const isInputAudioDeviceError = useSelector(getAudioInputError);
    const devicePermissionState = useSelector(selectMicrophoneDevicePermission);
    const isDevicePermissionError =
        (isInputAudioDeviceError && DEVICE_DENIED_ERRORS.has(isInputAudioDeviceError)) ||
        devicePermissionState === 'denied';

    const helpNoAccessLink = useSelector(getExternalLinks).helpNoAccess;

    const items = inputDevices.map(({ deviceId, label }) => ({
        itemKey: deviceId,
        label,
    }));
    const isDevicesNotFound = inputDevices.length <= 0;
    const setDevice = useCallback(
        async (id: React.Key | null) => {
            await deviceManager.stopPreviewAudio();
            logger.startFeatureDelta({
                feature: FEATURE.DEVICES,
                action: ACTION.SELECT_MIC,
            });
            setAudioInputDevice(String(id));
        },
        [setAudioInputDevice]
    );

    const select = (
        <SettingsSelect
            aria-label={i18n('settings-v2', 'sound-microphone')}
            items={items}
            selectedKey={currentInputDeviceId}
            onSelectionChange={setDevice}
            testId="input-devices-select"
            className={styles.selectDevice}
        />
    );
    const diagnosticSelectDevice = (
        <SettingsSelect
            aria-label={i18n('settings-v2', 'sound-microphone')}
            items={
                isDevicesNotFound
                    ? [
                          {
                              itemKey: SELECT_DEVICE_NOT_FOUND_KEY,
                              label: i18n('settings-v2', 'device-is-not-connected'),
                          },
                      ]
                    : items
            }
            selectedKey={isDevicesNotFound ? SELECT_DEVICE_NOT_FOUND_KEY : currentInputDeviceId}
            onSelectionChange={isDevicesNotFound ? undefined : setDevice}
            testId="input-devices-select"
            isDisabled={isDevicesNotFound}
            className={cn(styles.selectDevice, styles.selectDeviceDiagnostic)}
        />
    );
    return (
        <>
            {isDiagnosticAvailable ? (
                <>
                    {isDevicePermissionError ? (
                        <ListItem
                            label={i18n('settings-v2', 'diagnostic-access-granting-hint')}
                            interaction={false}
                            endSlot={
                                <Button
                                    elementType="a"
                                    rel="noopener noreferrer"
                                    href={helpNoAccessLink}
                                    target="_blank"
                                >
                                    {i18n('settings-v2', 'diagnostic-access-device-btn')}
                                </Button>
                            }
                        />
                    ) : (
                        <ListItem
                            label={diagnosticSelectDevice}
                            interaction={false}
                            endSlot={<InputDeviceDiagnosticButton isDisabled={isDevicesNotFound} />}
                        />
                    )}
                </>
            ) : (
                <ListItem label={i18n('settings-v2', 'sound-microphone')} interaction={false} endSlot={select} />
            )}
        </>
    );
};
